/**
 * FilterBuilder — a WYSIWYG, nested-group editor for the query language, opened
 * from the filter icon in QueryBar. It's the structural twin of the bar's
 * chips: a group has one combinator (All=AND / Any=OR) over its children, and
 * mixing AND/OR is done by nesting a group — exactly the parse-tree shape.
 *
 * It seeds from the current query text (parseQuery → astToBuilder), edits a tree
 * with stable ids, and on Apply serializes back (builderToQuery → onApply). Free
 * text and clauses the panel can't model as rows ride along as read-only "raw"
 * chips so nothing is dropped. All facet/operator/value knowledge is reused from
 * queryLang (QUERY_FIELDS / OPS_BY_KIND / suggestValues) — no new query logic.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QUERY_FIELDS, parseQuery, isValueSuggestField } from "../lib/queryLang.js";
import { astToBuilder, builderToQuery, UI_OPS, uiOpId, opsForField } from "../lib/queryBuilderModel.js";
import Select from "./Select.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";

const fieldLabel = (key) =>
  key.replace(/^mcp_server$/, "MCP server").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

// The facet catalog as combobox rows, each carrying a lowercased search string
// (key + aliases + description) so the field picker filters as you type.
const FIELD_ROWS = Object.entries(QUERY_FIELDS).map(([key, def]) => ({
  value: key,
  label: fieldLabel(key),
  search: [key, ...(def.aliases || []), def.desc || ""].join(" ").toLowerCase(),
}));
function filterFields(term, allow = null) {
  const t = String(term || "").trim().toLowerCase();
  let rows = allow ? FIELD_ROWS.filter((f) => allow.has(f.value)) : FIELD_ROWS;
  if (t) rows = rows.filter((f) => f.search.includes(t));
  return rows.map(({ value, label }) => ({ value, label }));
}

// ── id-keyed immutable tree helpers ─────────────────────────────────
let _idc = 0;
const newId = () => `fb${++_idc}`;
const newCond = () => ({ kind: "cond", _id: newId(), field: "agent", op: "eq", value: "", negated: false });
const newGroup = () => ({ kind: "group", _id: newId(), combinator: "and", children: [] });

function withIds(node) {
  if (node.kind === "group") return { ...node, _id: newId(), children: node.children.map(withIds) };
  return { ...node, _id: newId() };
}
function updateById(node, id, fn) {
  if (node._id === id) return fn(node);
  if (node.kind === "group") return { ...node, children: node.children.map((c) => updateById(c, id, fn)) };
  return node;
}
function removeById(node, id) {
  if (node.kind !== "group") return node;
  return { ...node, children: node.children.filter((c) => c._id !== id).map((c) => removeById(c, id)) };
}

// Combobox used for both the field picker (search the facet catalog as you
// type) and the value input (async value autocomplete). The dropdown is
// PORTALED to <body> and viewport-positioned so it escapes the panel's
// `overflow-auto` clip (and flips above when there's no room below).
//
//   freeText=false (field): the input shows the picked label; typing filters;
//                           you must pick a row to commit (onPick).
//   freeText=true  (value): the input text IS the value (onType commits live);
//                           rows merely suggest. Omit getOptions for a plain
//                           text field (no dropdown), e.g. free-text facets.
function ComboBox({ value, label, placeholder, getOptions, onPick, onType, freeText = false, ariaLabel, menuWidth }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menu, setMenu] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const hasMenu = typeof getOptions === "function";

  const place = useCallback(() => {
    const r = inputRef.current?.getBoundingClientRect();
    if (!r) return;
    const M = 8;
    const w = menuWidth || Math.max(r.width, 180);
    const left = Math.max(M, Math.min(r.left, window.innerWidth - w - M));
    const below = window.innerHeight - r.bottom - M;
    if (below >= 200 || below >= r.top) setMenu({ left, top: r.bottom + 2, width: w, maxHeight: Math.max(140, below) });
    else setMenu({ left, bottom: window.innerHeight - r.top + 2, width: w, maxHeight: Math.max(140, r.top - M) });
  }, [menuWidth]);

  useEffect(() => {
    if (!open || !hasMenu) return undefined;
    place();
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      Promise.resolve(getOptions(freeText ? value : term))
        .then((r) => { if (live) { setRows(Array.isArray(r) ? r : []); setLoading(false); } })
        .catch(() => { if (live) { setRows([]); setLoading(false); } });
    }, freeText ? 200 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [open, hasMenu, term, value, freeText, getOptions, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!inputRef.current?.contains(e.target) && !listRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    window.addEventListener("resize", place);
    return () => { document.removeEventListener("pointerdown", onDown, true); window.removeEventListener("resize", place); };
  }, [open, place]);

  const pick = (v) => { onPick(v); setOpen(false); setTerm(""); };
  const onKeyDown = (e) => {
    if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); }
    else if (e.key === "Enter" && open && rows[0]) { e.preventDefault(); pick(rows[0].value); }
  };

  const shown = freeText ? value : (open ? term : (label ?? value));
  return (
    <div className="min-w-0">
      <input
        ref={inputRef}
        type="text"
        value={shown}
        aria-label={ariaLabel}
        placeholder={freeText ? placeholder : (open ? (label || placeholder) : placeholder)}
        onFocus={() => { if (!hasMenu && !freeText) return; setTerm(""); setOpen(true); }}
        onKeyDown={onKeyDown}
        onChange={(e) => { if (freeText) onType?.(e.target.value); else setTerm(e.target.value); }}
        className="min-h-[30px] w-full rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
      />
      {open && hasMenu && menu && createPortal(
        <div
          ref={listRef}
          className="fixed z-[10010] overflow-auto border border-[color:var(--line-strong)] bg-[color:var(--panel)] shadow-lg"
          style={{ left: menu.left, top: menu.top, bottom: menu.bottom, width: menu.width, maxHeight: menu.maxHeight }}
        >
          {loading && rows.length === 0 ? (
            <div className="px-[9px] py-[6px] text-[11px] text-[color:var(--muted-soft)]">Searching…</div>
          ) : rows.length === 0 ? (
            <div className="px-[9px] py-[6px] text-[11px] text-[color:var(--muted-soft)]">No matches</div>
          ) : rows.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => pick(r.value)}
              className="flex w-full items-center justify-between gap-[8px] px-[9px] py-[5px] text-left text-[12px] leading-[1.2] text-[color:var(--ink)] hover:bg-[color:var(--green-soft)]"
            >
              <span className="min-w-0 truncate">{r.label || r.value}</span>
              {r.detail ? <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted-soft)]">{r.detail}</span> : null}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

const ICON_BTN = "grid h-[24px] w-[24px] shrink-0 place-items-center border border-[color:var(--line)] bg-[color:var(--control-bg)] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3";
// Condition-row remove: matches the 30px field/op/value control height so the
// row reads as one band instead of a short button beside taller inputs.
const COND_REMOVE = "grid h-[30px] w-[30px] shrink-0 place-items-center border border-[color:var(--line)] bg-[color:var(--control-bg)] text-[color:var(--muted-soft)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3";

function CondRow({ cond, suggestValues, allowedFields, onChange, onRemove }) {
  const opId = uiOpId(cond);
  const getFields = useCallback((term) => filterFields(term, allowedFields), [allowedFields]);
  const setOp = (id) => {
    const m = UI_OPS[id];
    onChange({ ...cond, op: m.op, negated: m.negated });
  };
  // Switching field can invalidate the current operator (e.g. `gt` on a string
  // field); clamp to `is` (eq) when the held op isn't offered for the new field.
  const setField = (field) => {
    const next = { ...cond, field };
    const valid = opsForField(field).some((o) => o.value === uiOpId(next));
    onChange(valid ? next : { ...next, op: "eq", negated: false });
  };
  return (
    // Grid (not flex) so each w-full Select fills only its own column — a flex
    // row let the field dropdown grow to 100% and push op/value off-screen.
    <div className="grid grid-cols-[8rem_7.5rem_minmax(0,1fr)_auto] items-center gap-[5px]">
      <ComboBox
        value={cond.field}
        label={fieldLabel(cond.field)}
        placeholder="field…"
        getOptions={getFields}
        onPick={setField}
        ariaLabel="Field"
        menuWidth={240}
      />
      <Select
        value={opId}
        onChange={setOp}
        options={opsForField(cond.field)}
        ariaLabel="Operator"
        className="min-w-0"
        menuWidth={170}
      />
      <ComboBox
        value={cond.value}
        placeholder={isValueSuggestField(cond.field) ? "value…" : "value"}
        freeText
        getOptions={isValueSuggestField(cond.field) && typeof suggestValues === "function"
          ? (term) => suggestValues(cond.field, term)
          : undefined}
        onPick={(v) => onChange({ ...cond, value: v })}
        onType={(v) => onChange({ ...cond, value: v })}
        ariaLabel="Value"
      />
      <button type="button" aria-label="Remove condition" className={COND_REMOVE} onClick={onRemove}>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M4 4l8 8M12 4l-8 8" /></svg>
      </button>
    </div>
  );
}

function RawRow({ node, onRemove }) {
  return (
    <div className="flex items-center gap-[6px] rounded-[2px] border border-dashed border-[color:var(--line)] px-[9px] py-[5px]">
      <span className="min-w-0 flex-1"><OverflowTooltip text={node.text} className="text-[11px] text-[color:var(--muted)]" /></span>
      <span className="shrink-0 text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">edit in bar</span>
      <button type="button" aria-label="Remove clause" className={ICON_BTN} onClick={onRemove}>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M4 4l8 8M12 4l-8 8" /></svg>
      </button>
    </div>
  );
}

const COMBINATOR_OPTIONS = [{ value: "and", label: "All (AND)" }, { value: "or", label: "Any (OR)" }];

function GroupView({ node, depth, suggestValues, allowedFields, onChange, onRemove }) {
  const setChild = (child) => onChange((g) => updateById(g, child._id, () => child));
  const removeChild = (id) => onChange((g) => removeById(g, id));
  const addCond = () => onChange((g) => updateById(g, node._id, (n) => ({ ...n, children: [...n.children, newCond()] })));
  const addGroup = () => onChange((g) => updateById(g, node._id, (n) => ({ ...n, children: [...n.children, newGroup()] })));
  const setCombinator = (combinator) => onChange((g) => updateById(g, node._id, (n) => ({ ...n, combinator })));

  return (
    <div className={depth === 0 ? "grid gap-[6px]" : "grid gap-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] p-[7px]"}>
      <div className="flex items-center gap-[6px]">
        <span className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">Match</span>
        <Select value={node.combinator} onChange={setCombinator} options={COMBINATOR_OPTIONS} ariaLabel="Combinator" variant="chip" />
        {depth > 0 ? (
          // Group-scoped remove, styled as the header's right-hand eyebrow (not a
          // boxed ✕) so it reads as a group action rather than blending into the
          // column of per-row remove buttons below it.
          <button
            type="button"
            aria-label="Remove group"
            className="ml-auto text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)] transition-colors hover:text-[color:var(--ink)]"
            onClick={onRemove}
          >
            Remove group
          </button>
        ) : null}
      </div>
      <div className="grid gap-[5px]">
        {node.children.length === 0 ? (
          <div className="text-[11px] text-[color:var(--muted-soft)]">No conditions yet.</div>
        ) : node.children.map((c) =>
          c.kind === "group" ? (
            <GroupView key={c._id} node={c} depth={depth + 1} suggestValues={suggestValues} allowedFields={allowedFields} onChange={onChange} onRemove={() => removeChild(c._id)} />
          ) : c.kind === "raw" ? (
            <RawRow key={c._id} node={c} onRemove={() => removeChild(c._id)} />
          ) : (
            <CondRow key={c._id} cond={c} suggestValues={suggestValues} allowedFields={allowedFields} onChange={setChild} onRemove={() => removeChild(c._id)} />
          ),
        )}
      </div>
      <div className="flex gap-[6px]">
        <button type="button" onClick={addCond} className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[4px] text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">+ Condition</button>
        <button type="button" onClick={addGroup} className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[4px] text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">+ Group</button>
      </div>
    </div>
  );
}

export default function FilterBuilder({ query = "", suggestValues, fields = null, onApply, onClose, anchorRef }) {
  // Seed the tree once from the current query; assign stable ids for editing.
  const [root, setRoot] = useState(() => withIds(astToBuilder(parseQuery(query).ast)));
  // Optional field allowlist (e.g. the scope steer's no-join set); null = all.
  const allowedFields = useMemo(() => (fields ? new Set(fields) : null), [fields]);
  const [pos, setPos] = useState(null);

  // Boundary-aware placement: anchor below the icon, but flip above when the
  // space below is cramped and there's more above; cap maxHeight to the
  // available viewport space so the panel scrolls internally instead of
  // spilling off-screen as conditions/groups are added. Recomputed on resize.
  // It hangs from its field: under the icon's right edge where the field is
  // wide enough to hold it, else from the field's left edge (a field in a
  // narrow drawer opens it over the page beside, not past the drawer's left).
  const recompute = useCallback(() => {
    const r = anchorRef?.current?.getBoundingClientRect();
    if (!r) return;
    const M = 8;
    const width = 540;
    const fieldLeft = anchorRef.current.closest?.("[data-query-field]")?.getBoundingClientRect().left ?? r.left;
    const left = Math.max(M, Math.min(Math.max(fieldLeft, r.right - width), window.innerWidth - width - M));
    const below = window.innerHeight - r.bottom - M;
    const above = r.top - M;
    if (below >= 260 || below >= above) {
      setPos({ top: r.bottom + 4, left, maxHeight: Math.max(160, below) });
    } else {
      setPos({ bottom: window.innerHeight - r.top + 4, left, maxHeight: Math.max(160, above) });
    }
  }, [anchorRef]);
  useLayoutEffect(() => { recompute(); }, [recompute]);
  useEffect(() => {
    // Re-anchor to the button on scroll/resize (rAF-throttled) so the panel
    // tracks its trigger instead of floating away when the user scrolls the
    // trace log. `capture` catches scrolls on any ancestor scroll container.
    let raf = 0;
    const onMove = () => { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; recompute(); }); };
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [recompute]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onChange = useCallback((updater) => { setRoot((cur) => updater(cur)); }, []);

  const preview = useMemo(() => builderToQuery(root), [root]);

  const apply = () => { onApply?.(builderToQuery(root)); onClose?.(); };

  if (!pos) return null;
  // No click-outside dismissal — an Apply-flow builder shouldn't silently
  // discard an in-progress filter; close via Escape / Cancel / the icon toggle.
  return createPortal(
    <>
      <div
        role="dialog"
        aria-label="Filter builder"
        className="fixed z-[9999] grid w-[540px] gap-[8px] overflow-auto border border-[color:var(--line)] bg-[color:var(--panel)] p-[10px] shadow-xl"
        style={{ top: pos.top, bottom: pos.bottom, left: pos.left, maxHeight: pos.maxHeight }}
      >
        <div className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">Filters</div>
        <GroupView node={root} depth={0} suggestValues={suggestValues} allowedFields={allowedFields} onChange={onChange} onRemove={() => {}} />
        <div className="grid gap-[2px]">
          <span className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">Query</span>
          <code className="block min-h-[18px] break-words rounded-[2px] bg-[color:var(--bg)] px-[8px] py-[5px] text-[11px] leading-[1.4] text-[color:var(--muted)] [font-family:inherit]">{preview || "—"}</code>
        </div>
        <div className="flex justify-end gap-[6px]">
          <button type="button" onClick={onClose} className="rounded-[2px] border border-[color:var(--line)] bg-transparent px-[12px] py-[5px] text-[12px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">Cancel</button>
          <button type="button" onClick={apply} className="rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[12px] py-[5px] text-[12px] font-medium text-[color:var(--ink)] hover:border-[color:var(--green)]">Apply</button>
        </div>
      </div>
    </>,
    document.body,
  );
}
